import { useCallback, useEffect, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";

import Box from "@mui/material/Box";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Typography from "@mui/material/Typography";
import type { SxProps } from "@mui/material/styles";

import { values } from "lodash";

import { misc } from "app/themes/colors";
import { InventoryProductSpecification } from "services/types/inventory-products.type";

import { InventoryType } from "../inventory-product-form/inventory-product-form.component";
import {
  getDefaultValues,
  getProductSpecificationType,
  mapValuesToSpecification
} from "./inventory-product-specification-form.util";
import DomeSpecificationForm, {
  DomeSpecificationValues
} from "./specification-forms/dome-specification-form.component";
import EarmoldSpecificationForm, {
  EarmoldSpecificationValues
} from "./specification-forms/earmold-specification-form.component";
import HearingAidSpecificationForm, {
  HearingAidSpecificationValues
} from "./specification-forms/hearing-aid-specification.component";
import OtherSpecificationForm, {
  OtherSpecificationValues
} from "./specification-forms/other-specification-form.component";
import ReceiverSpecificationForm, {
  ReceiverSpecificationValues
} from "./specification-forms/receiver-specification-form.component";
import TubeSpecificationForm, {
  TubeSpecificationValues
} from "./specification-forms/tube-specification-form.component";

export type InventoryProductSpecificationValues = {
  hearing_aid?: HearingAidSpecificationValues | null;
  receiver?: ReceiverSpecificationValues | null;
  earmold?: EarmoldSpecificationValues | null;
  tube?: TubeSpecificationValues | null;
  dome?: DomeSpecificationValues | null;
  other?: OtherSpecificationValues | null;
};

type InventoryProductSpecificationFormProps = {
  header?: string;
  type: number | string;
  subtype?: number | string | null;
  sx?: SxProps;
  formSx?: SxProps;
  headerSx?: SxProps;
  initialValues?: InventoryProductSpecification | null;
  onChange: (
    data: InventoryProductSpecification | null,
    isValid: boolean
  ) => void;
  inventoryType: InventoryType;
};

export default function InventoryProductSpecificationForm({
  header = "Specifications",
  initialValues,
  type,
  subtype,
  sx,
  formSx,
  headerSx,
  onChange,
  inventoryType
}: InventoryProductSpecificationFormProps) {
  const productForm = getProductSpecificationType(type, subtype);
  const { isHearingAid, isReceiver, isEarmold, isTube, isDome, isOther } =
    productForm;

  const defaultValues = useMemo(() => {
    return getDefaultValues(productForm, initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, subtype, initialValues]);

  const formMethods = useForm<InventoryProductSpecificationValues>({
    mode: "onBlur",
    defaultValues
  });

  const {
    control,
    reset,
    trigger,
    formState: { isDirty, isValidating, isValid }
  } = formMethods;

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const formData = useWatch({ control });

  const sendFormData = useCallback(
    (data: InventoryProductSpecificationValues) => {
      if (!isValidating && isDirty) {
        onChange(mapValuesToSpecification(data, inventoryType), isValid);
      }
    },
    [isValidating, isValid, isDirty, onChange, inventoryType]
  );

  useEffect(() => {
    if (!values(productForm).find(Boolean)) {
      onChange(null, true);
    }
  }, [productForm, onChange]);

  // Propagate edits even while invalid so the parent can disable its Save
  // button; gating this on isValid let a stale specification be submitted
  // silently (ENG-1557). trigger() surfaces the blocking field error, which
  // onBlur mode alone never shows for an untouched required field.
  useEffect(() => {
    sendFormData(formData as InventoryProductSpecificationValues);
    if (!isValid && isDirty) {
      trigger();
    }
  }, [formData, sendFormData, isValid, isDirty, trigger]);

  if (!values(productForm).find(Boolean)) {
    return null;
  }

  return (
    <ClickAwayListener
      onClickAway={() => {
        sendFormData(formData as InventoryProductSpecificationValues);
      }}
    >
      <Box sx={{ width: "100%", ...sx }}>
        {Boolean(header) && (
          <Typography
            variant="h5"
            component="h3"
            fontWeight={400}
            fontSize={20}
            color={misc.grayAsphalt}
            mb={2.5}
            sx={headerSx}
          >
            {header}
          </Typography>
        )}
        {isHearingAid && (
          <HearingAidSpecificationForm control={control} sx={formSx} />
        )}
        {isReceiver && (
          <ReceiverSpecificationForm control={control} sx={formSx} />
        )}
        {isEarmold && (
          <EarmoldSpecificationForm
            inventoryType={inventoryType}
            control={control}
            sx={formSx}
          />
        )}
        {isTube && <TubeSpecificationForm control={control} />}
        {isDome && <DomeSpecificationForm control={control} />}
        {isOther && <OtherSpecificationForm control={control} />}
      </Box>
    </ClickAwayListener>
  );
}
